<script lang="ts">
  import { onMount } from "svelte";
  import {
    SYNC_DASHBOARD_REFRESH_EVENT,
    triggerOpportunityDataRefresh,
  } from "../../lib/syncDashboardRefresh";
  import {
    beginHubspotOAuthLogin,
    ensureHubspotAuthenticated,
    extractApiMessage,
    fetchJsonWithHubspotAuth,
    HUBSPOT_AUTH_CHANGED_EVENT,
    initializeHubspotOpportunityFields,
    isHubspotAuthRequiredError,
    logoutHubspotSession,
    refreshHubspotSessionWithToken,
    syncOpportunityToHubspotById,
  } from "../../lib/hubspotAuth";
  import SystemsOverview from "./systemsOverview.svelte";
  import SyncHeaderHost from "./remotes/SyncHeaderHost.svelte";
  import HubspotAuthPanelHost from "./remotes/HubspotAuthPanelHost.svelte";
  import KpiBarHost from "./remotes/KpiBarHost.svelte";
  import NavigationTabsHost from "./remotes/NavigationTabsHost.svelte";
  import OpportunitiesTableHost from "./remotes/OpportunitiesTableHost.svelte";
  import OpportunityDrawerHost from "./remotes/OpportunityDrawerHost.svelte";
  import ActivityFeed from "./activityFeed.svelte";
  import ConflictsSummaryHost from "./remotes/ConflictsSummaryHost.svelte";
  import EngagementInvitationsPanel from "../engagementInvitationsPanel.svelte";
  import type { SyncStatus, OpportunitiesResponse, OpportunityDetailed, Activity } from "./types";

  // @ts-ignore
  const baseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;

  let syncStatus: SyncStatus | null = null;
  let opportunitiesData: OpportunitiesResponse | null = null;
  let activityFeed: Activity[] = [];
  let loading = false;
  let error = "";
  let isVisible = false;
  let lastSyncTime = "";
  let retrying = false;
  let syncProgress: { current: number; total: number; message: string } | null = null;
  let activeTab = "overview";
  let selectedOpportunity: OpportunityDetailed | null = null;
  let drawerOpen = false;
  let filterStatus = "all";
  let hubspotAuthChecked = false;
  let isHubspotAuthenticated = false;
  let authStatusMessage = "";
  let authResponseMessage = "";
  let authActionLoading = false;
  let logoutPending = false;
  let showRefreshInput = false;
  let refreshTokenInput = "";
  let hubspotConnectedNoticeVisible = false;
  let hubspotConnectedNoticeTimeout: ReturnType<typeof setTimeout> | null = null;
  let postSyncRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

  const hubspotFeatureSummary =
    "Connect HubSpot to enable Sync from HubSpot, Sync to HubSpot actions, and popup editor feedback in the new Opportunity Sync UI.";

  const clearAuthenticatedDashboardData = (): void => {
    opportunitiesData = null;
    syncProgress = null;
    drawerOpen = false;
    selectedOpportunity = null;
  };

  const clearDashboardData = (): void => {
    syncStatus = null;
    error = "";
    clearAuthenticatedDashboardData();
  };

  const setHubspotAuthMessage = (): void => {
    authStatusMessage = isHubspotAuthenticated
      ? "HubSpot is connected for Opportunity Sync."
      : hubspotFeatureSummary;
  };

  const clearHubspotConnectedNoticeTimeout = (): void => {
    if (hubspotConnectedNoticeTimeout) {
      clearTimeout(hubspotConnectedNoticeTimeout);
      hubspotConnectedNoticeTimeout = null;
    }
  };

  const clearPostSyncRefreshTimeout = (): void => {
    if (postSyncRefreshTimeout) {
      clearTimeout(postSyncRefreshTimeout);
      postSyncRefreshTimeout = null;
    }
  };

  const scheduleHubspotConnectedNoticeHide = (): void => {
    clearHubspotConnectedNoticeTimeout();
    hubspotConnectedNoticeVisible = true;
    hubspotConnectedNoticeTimeout = setTimeout(() => {
      hubspotConnectedNoticeVisible = false;
      hubspotConnectedNoticeTimeout = null;
    }, 6000);
  };

  const refreshHubspotAuthState = async (
    options: { tryRestore?: boolean; loadData?: boolean } = {}
  ): Promise<boolean> => {
    const { tryRestore = true, loadData = false } = options;

    refreshTokenInput = localStorage.getItem("hubspotRefreshToken") ?? refreshTokenInput;

    let authenticated = false;

    try {
      authenticated = await ensureHubspotAuthenticated({ tryRestore });
    } catch (err) {
      if (!authenticated) {
        console.error("Failed to check HubSpot authentication status:", err);
      }
    }

    isHubspotAuthenticated = authenticated;
    hubspotAuthChecked = true;
    setHubspotAuthMessage();

    if (authenticated) {
      authResponseMessage = "";
      scheduleHubspotConnectedNoticeHide();
    } else {
      clearHubspotConnectedNoticeTimeout();
      hubspotConnectedNoticeVisible = false;
      clearAuthenticatedDashboardData();
    }

    if (loadData && isVisible) {
      await fetchAllData();
    }

    return authenticated;
  };

  // Load visibility preference from localStorage
  onMount(() => {
    const savedVisibility = localStorage.getItem("syncStatusDashboardVisible");
    if (savedVisibility !== null) {
      isVisible = savedVisibility === "true";
    } else {
      isVisible = true;
      localStorage.setItem("syncStatusDashboardVisible", "true");
    }
    updateLastSyncTime();

    void refreshHubspotAuthState({ loadData: true });

    // Setup auto-refresh for activity feed (every 30 seconds)
    const interval = setInterval(() => {
      if (isVisible) fetchActivityFeed();
    }, 30000);

    const handleAuthChanged = () => {
      void refreshHubspotAuthState({ tryRestore: false, loadData: true });
    };
    const handleDashboardRefresh = () => {
      if (isVisible) void refreshAfterSync();
    };
    window.addEventListener(HUBSPOT_AUTH_CHANGED_EVENT, handleAuthChanged);
    window.addEventListener(SYNC_DASHBOARD_REFRESH_EVENT, handleDashboardRefresh);

    return () => {
      clearInterval(interval);
      clearHubspotConnectedNoticeTimeout();
      clearPostSyncRefreshTimeout();
      window.removeEventListener(HUBSPOT_AUTH_CHANGED_EVENT, handleAuthChanged);
      window.removeEventListener(SYNC_DASHBOARD_REFRESH_EVENT, handleDashboardRefresh);
    };
  });

  const updateLastSyncTime = () => {
    const now = new Date();
    lastSyncTime = now.toISOString();
  };

  const fetchAllData = async (options: { silent?: boolean } = {}) => {
    const tasks: Promise<unknown>[] = [fetchSyncStatus(options)];

    tasks.push(fetchActivityFeed());
    if (isHubspotAuthenticated) {
      tasks.push(fetchOpportunitiesDetailed());
    } else {
      clearAuthenticatedDashboardData();
    }

    await Promise.all(tasks);

    // Update selected opportunity if drawer is open to reflect sync status changes
    if (drawerOpen && selectedOpportunity && opportunitiesData?.opportunities) {
      const updated = opportunitiesData.opportunities.find(
        (o) => String(o.id) === String(selectedOpportunity?.id)
      );
      if (updated) {
        selectedOpportunity = updated;
      }
    }
  };

  const fetchSyncStatus = async (options: { silent?: boolean } = {}) => {
    if (!options.silent) loading = true;
    error = "";
    try {
      const res = await fetch(`${baseUrl}/sync/status-overview`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      syncStatus = await res.json();
      updateLastSyncTime();
    } catch (err: any) {
      error = err?.message || "Failed to fetch sync status";
      console.error("Failed to fetch sync status:", err);
    } finally {
      if (!options.silent) loading = false;
    }
  };

  const fetchOpportunitiesDetailed = async () => {
    try {
      const res = await fetch(`${baseUrl}/sync/opportunities-detailed`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const raw = await res.json();
      opportunitiesData = Array.isArray(raw)
        ? { opportunities: raw }
        : raw;
    } catch (err: any) {
      console.error("Failed to fetch opportunities:", err);
    }
  };

  const fetchActivityFeed = async () => {
    try {
      const res = await fetch(`${baseUrl}/sync/activity-feed`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      activityFeed = data.activities;
    } catch (err: any) {
      console.error("Failed to fetch activity feed:", err);
    }
  };

  const refreshAfterSync = async () => {
    clearPostSyncRefreshTimeout();
    if (!isVisible) return;
    await fetchAllData({ silent: true }).catch(() => undefined);
    postSyncRefreshTimeout = setTimeout(() => {
      if (!isVisible) return;
      void fetchAllData({ silent: true });
      postSyncRefreshTimeout = setTimeout(() => {
        if (isVisible) void fetchAllData({ silent: true });
      }, 3000);
    }, 2000);
  };

  const toggleVisibility = () => {
    isVisible = !isVisible;
    localStorage.setItem("syncStatusDashboardVisible", String(isVisible));
    if (isVisible && isHubspotAuthenticated) fetchAllData();
  };

  const runSyncWithProgress = async (
    steps: Array<{
      progress: number;
      message: string;
      action?: () => Promise<any>;
    }>
  ) => {
    loading = true;
    error = "";
    syncProgress = { current: 0, total: 100, message: "Starting sync..." };
    try {
      for (const step of steps) {
        syncProgress = { current: step.progress, total: 100, message: step.message };
        if (step.action) await step.action();
      }
      syncProgress = { current: 100, total: 100, message: "Sync completed!" };
      syncProgress = { current: 100, total: 100, message: "Refreshing dashboard data..." };
      triggerOpportunityDataRefresh();
      setTimeout(() => { syncProgress = null; }, 1000);
    } catch (err: any) {
      error = err?.message || "Sync failed.";
      syncProgress = { current: 0, total: 100, message: error };
      if (!isHubspotAuthRequiredError(err)) {
        console.error("Sync failed:", err);
      }
      await fetchAllData({ silent: true }).catch(() => undefined);
      setTimeout(() => { syncProgress = null; }, 3000);
    } finally {
      loading = false;
    }
  };

  const fetchSyncAction = async (url: string, fallbackMessage: string) => {
    const response = await fetch(url, { method: "GET" });
    const text = await response.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!response.ok || data?.success === false) {
      throw new Error(extractApiMessage(data, text, fallbackMessage));
    }
    return data;
  };

  const initHubspotV2 = async (): Promise<boolean> => {
    const result = await initializeHubspotOpportunityFields();
    if (!result.success && !result.authRequired) {
      console.error("Failed to initialize HubSpot V2 fields:", result.message);
    }
    return result.success;
  };

  const handleSyncNow = async () => {
    if (!isHubspotAuthenticated) {
      authResponseMessage = "HubSpot login is required before running Sync Now.";
      return;
    }
    await runSyncWithProgress([
      { progress: 5, message: "Starting full sync (AWS + HubSpot)..." },
      {
        progress: 15,
        message: "Fetching opportunities from AWS...",
        action: () => fetchSyncAction(`${baseUrl}/alace/sync/from-aws`, "Failed to sync from AWS."),
      },
      {
        progress: 30,
        message: "Pushing local changes to AWS...",
        action: () => fetchSyncAction(`${baseUrl}/alace/sync/to-aws`, "Failed to sync to AWS."),
      },
      {
        progress: 45,
        message: "Initializing HubSpot fields...",
        action: async () => {
          const initialized = await initHubspotV2();
          if (!initialized) {
            throw new Error("HubSpot V2 field init failed. Please retry.");
          }
        },
      },
      {
        progress: 60,
        message: "Fetching opportunities from HubSpot...",
        action: () =>
          fetchJsonWithHubspotAuth(`${baseUrl}/hubspot-sync/from-hubspot`, { method: "POST" }, {
            authRequiredMessage: "HubSpot login is required before syncing opportunities from HubSpot.",
          }).then((res) => {
            if (!res.response.ok || res.data?.success === false) {
              throw new Error(extractApiMessage(res.data, res.text, "Failed to sync from HubSpot."));
            }
          }),
      },
      {
        progress: 78,
        message: "Pushing changes to HubSpot...",
        action: () =>
          fetchJsonWithHubspotAuth(`${baseUrl}/hubspot-sync/to-hubspot`, { method: "POST" }, {
            authRequiredMessage: "HubSpot login is required before syncing to HubSpot.",
          }).then((res) => {
            if (!res.response.ok || res.data?.success === false) {
              throw new Error(extractApiMessage(res.data, res.text, "Failed to sync to HubSpot."));
            }
          }),
      },
      {
        progress: 90,
        message: "Finalizing sync...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };

  const handleSyncFromAws = async () => {
    await runSyncWithProgress([
      { progress: 20, message: "Connecting to AWS..." },
      {
        progress: 55,
        message: "Fetching opportunities from AWS...",
        action: () => fetchSyncAction(`${baseUrl}/alace/sync/from-aws`, "Failed to sync from AWS."),
      },
      {
        progress: 85,
        message: "Processing received data...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };

  const handleSyncToAws = async () => {
    await runSyncWithProgress([
      { progress: 20, message: "Preparing local data..." },
      {
        progress: 55,
        message: "Pushing changes to AWS...",
        action: () => fetchSyncAction(`${baseUrl}/alace/sync/to-aws`, "Failed to sync to AWS."),
      },
      {
        progress: 85,
        message: "Confirming sync with AWS...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };

  const handleSyncFromHubspot = async () => {
    if (!isHubspotAuthenticated) {
      authResponseMessage = "HubSpot login is required before syncing from HubSpot.";
      return;
    }
    await runSyncWithProgress([
      { progress: 20, message: "Connecting to HubSpot..." },
      {
        progress: 40,
        message: "Initializing HubSpot fields...",
        action: async () => {
          const initialized = await initHubspotV2();
          if (!initialized) {
            throw new Error("HubSpot V2 field init failed. Please retry.");
          }
        },
      },
      {
        progress: 65,
        message: "Fetching opportunities from HubSpot...",
        action: () =>
          fetchJsonWithHubspotAuth(`${baseUrl}/hubspot-sync/from-hubspot`, { method: "POST" }, {
            authRequiredMessage: "HubSpot login is required before syncing opportunities from HubSpot.",
          }).then((res) => {
            if (!res.response.ok || res.data?.success === false) {
              throw new Error(extractApiMessage(res.data, res.text, "Failed to sync from HubSpot."));
            }
          }),
      },
      {
        progress: 85,
        message: "Processing received data...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };

  const handleSyncToHubspot = async () => {
    if (!isHubspotAuthenticated) {
      authResponseMessage = "HubSpot login is required before syncing to HubSpot.";
      return;
    }
    await runSyncWithProgress([
      { progress: 20, message: "Connecting to HubSpot..." },
      {
        progress: 40,
        message: "Initializing HubSpot fields...",
        action: async () => {
          const initialized = await initHubspotV2();
          if (!initialized) {
            throw new Error("HubSpot V2 field init failed. Please retry.");
          }
        },
      },
      {
        progress: 65,
        message: "Pushing changes to HubSpot...",
        action: () =>
          fetchJsonWithHubspotAuth(`${baseUrl}/hubspot-sync/to-hubspot`, { method: "POST" }, {
            authRequiredMessage: "HubSpot login is required before syncing to HubSpot.",
          }).then((res) => {
            if (!res.response.ok || res.data?.success === false) {
              throw new Error(extractApiMessage(res.data, res.text, "Failed to sync to HubSpot."));
            }
          }),
      },
      {
        progress: 85,
        message: "Confirming sync with HubSpot...",
        action: () => new Promise((resolve) => setTimeout(resolve, 1000)),
      },
    ]);
  };
  const handleLogin = async (): Promise<void> => {
    authActionLoading = true;
    logoutPending = false;
    authResponseMessage = "";

    try {
      await beginHubspotOAuthLogin();
    } catch (error: unknown) {
      authResponseMessage =
        error instanceof Error ? error.message : "Could not fetch HubSpot login URL.";
      authActionLoading = false;
    }
  };

  const handleLogout = async (): Promise<void> => {
    authActionLoading = true;
    logoutPending = true;
    authResponseMessage = "";

    try {
      const result = await logoutHubspotSession();
      refreshTokenInput = "";
      showRefreshInput = false;

      if (!result.success) {
        authResponseMessage = result.message;
      }
    } catch (error: unknown) {
      authResponseMessage =
        error instanceof Error ? error.message : "Failed to log out from HubSpot.";
    } finally {
      logoutPending = false;
      authActionLoading = false;
      await refreshHubspotAuthState({ tryRestore: false, loadData: isVisible });
    }
  };

  const handleSubmitRefreshToken = async (): Promise<void> => {
    authActionLoading = true;
    logoutPending = false;
    authResponseMessage = "";

    const result = await refreshHubspotSessionWithToken(refreshTokenInput);
    if (result.success) {
      showRefreshInput = false;
      await refreshHubspotAuthState({ tryRestore: false, loadData: true });
    } else {
      authResponseMessage = result.message;
    }

    authActionLoading = false;
  };

  const handleSyncOpportunityToAws = async (opp: OpportunityDetailed) => {
    if (!opp.id) {
      console.error("Cannot sync to AWS: opportunity ID is missing");
      return;
    }

    try {
      // Fixed URL: added /alace prefix to match the working backend route
      const res = await fetch(`${baseUrl}/alace/sync/opportunity/${opp.id}`, {
        method: 'POST',
      });
      
      if (!res.ok) {
        throw new Error(`Failed to sync to AWS: ${res.statusText}`);
      }
      
      triggerOpportunityDataRefresh();
    } catch (err: any) {
      console.error("AWS sync failed:", err);
      alert(`Failed to sync to AWS: ${err.message}`);
    }
  };

  const handleSyncOpportunityToHubspot = async (opp: OpportunityDetailed) => {
    if (!opp.id) {
      console.error("Cannot sync to HubSpot: opportunity ID is missing");
      return;
    }

    try {
      const result = await syncOpportunityToHubspotById(Number(opp.id));
      if (result.authRequired) {
        return;
      }
      if (!result.success && !result.authRequired) {
        throw new Error(result.message || "Failed to sync to HubSpot");
      }
      triggerOpportunityDataRefresh();
    } catch (err: any) {
      if (!isHubspotAuthRequiredError(err)) {
        console.error("HubSpot sync failed:", err);
        alert(`Failed to sync to HubSpot: ${err.message}`);
      }
    }
  };

  const selectOpportunity = (opp: OpportunityDetailed) => {
    selectedOpportunity = opp;
    drawerOpen = true;
  };

  const closeDrawer = () => {
    drawerOpen = false;
    setTimeout(() => {
      selectedOpportunity = null;
    }, 300);
  };

  const handleKPIClick = (status: string) => {
    filterStatus = status;
    activeTab = "opportunities";
  };

  const handleTabChange = (tab: string) => activeTab = tab;

  $: totalOpportunities = syncStatus?.totalOpportunities ?? syncStatus?.aws?.totalInAws ?? 0;
  $: fullySynced = syncStatus?.fullySyncedAcrossSystems ?? 0;
  $: syncInProgress = (syncStatus?.aws?.notSynced ?? 0) + (syncStatus?.hubspot?.notSyncedToLocal ?? 0);
  $: conflicts = opportunitiesData?.conflicts ?? syncStatus?.aws?.conflicts ?? 0;
  $: errors = opportunitiesData?.errors ?? syncStatus?.aws?.errors ?? 0;
  $: visibleTotalOpportunities = isHubspotAuthenticated
    ? totalOpportunities
    : syncStatus?.aws?.totalInAws ?? totalOpportunities;
  $: visibleFullySynced = isHubspotAuthenticated
    ? fullySynced
    : syncStatus?.aws?.syncedToSharedDb ?? 0;
  $: visibleSyncInProgress = isHubspotAuthenticated
    ? syncInProgress
    : syncStatus?.aws?.notSynced ?? 0;
  $: visibleConflicts = syncStatus?.aws?.conflicts ?? conflicts;
  $: visibleErrors = isHubspotAuthenticated ? errors : syncStatus?.aws?.errors ?? 0;
  $: dashboardApiError = Boolean(error || syncStatus?.hubspot?.apiError || syncStatus?.hubspot?.error);
  $: dashboardContentVisible = isVisible;
  $: showAuthenticatedHubspotNotice = hubspotAuthChecked
    && isHubspotAuthenticated
    && hubspotConnectedNoticeVisible;
  
  // Global health status
  $: globalHealth = dashboardApiError ? "Error" : visibleConflicts > 0 ? "Degraded" : "Healthy";
  $: healthIcon = globalHealth === "Healthy" ? "🟢" : globalHealth === "Degraded" ? "🟡" : "🔴";

  $: syncActions = [
    {
      label: "Sync Everything",
      description: "Full bidirectional sync (AWS + HubSpot)",
      badgeClass: "bg-indigo-500/20 text-indigo-400",
      iconPaths: [
        "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
      ],
      handler: handleSyncNow,
    },
    {
      label: "Sync from AWS",
      description: "Pull latest data from AWS",
      badgeClass: "bg-sky-500/20 text-sky-400",
      iconPaths: ["M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"],
      handler: handleSyncFromAws,
    },
    {
      label: "Sync to AWS",
      description: "Push local changes to AWS",
      badgeClass: "bg-emerald-500/20 text-emerald-400",
      iconPaths: ["M17 4v12M7 20v-12", "M21 16l-4 4-4-4M3 8l4-4 4 4"],
      handler: handleSyncToAws,
    },
    ...(isHubspotAuthenticated
      ? [
          {
            label: "Sync from HubSpot",
            description: "Pull opportunities from HubSpot",
            badgeClass: "bg-orange-500/20 text-orange-400",
            iconPaths: ["M17 4v12M7 20v-12", "M21 16l-4 4-4-4M3 8l4-4 4 4"],
            handler: handleSyncFromHubspot,
          },
          {
            label: "Sync to HubSpot",
            description: "Push local changes to HubSpot",
            badgeClass: "bg-green-500/20 text-green-400",
            iconPaths: ["M17 4v12M7 20v-12", "M21 16l-4 4-4-4M3 8l4-4 4 4"],
            handler: handleSyncToHubspot,
          },
        ]
      : []),
  ];

  // Filtered opportunities
  $: filteredOpportunities = (opportunitiesData?.opportunities || []).filter(
    (opp) => {
      if (filterStatus === "all") return true;
      if (filterStatus === "synced") return opp.status === "synced";
      if (filterStatus === "pending") return opp.status === "not_synced";
      if (filterStatus === "error") return opp.status === "error";
      if (filterStatus === "conflict") return opp.status === "conflict";
      return true;
    },
  );
</script>

<div class="mb-8 font-sans">
  <SyncHeaderHost
    {lastSyncTime}
    {globalHealth}
    {healthIcon}
    {loading}
    {isVisible}
    {syncProgress}
    {syncActions}
    controlsDisabled={!isVisible}
    onToggleVisibility={toggleVisibility}
  />

  {#if hubspotAuthChecked && (!isHubspotAuthenticated || showAuthenticatedHubspotNotice)}
    <div class="px-0 pt-3 sm:pt-5 pb-0">
      {#if !isHubspotAuthenticated}
        <HubspotAuthPanelHost
          isAuthenticated={isHubspotAuthenticated}
          responseMessage={authResponseMessage}
          loading={authActionLoading}
          {showRefreshInput}
          refreshTokenValue={refreshTokenInput}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onToggleRefreshInput={() => {
            showRefreshInput = !showRefreshInput;
            authResponseMessage = "";
          }}
          onRefreshTokenInput={(value) => {
            refreshTokenInput = value;
          }}
          onSubmitRefreshToken={handleSubmitRefreshToken}
        />
      {:else if showAuthenticatedHubspotNotice}
        <div class="mb-6 flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#24283a] px-5 py-4 text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)] sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-300">
              HubSpot Connected
            </p>
            <p class="mt-1 text-sm text-white/70">
              Your HubSpot account is ready to use.
            </p>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if dashboardContentVisible}
    <div class="px-0 py-3 sm:py-5">
      {#if loading && !syncStatus}
        <div
          class="text-center py-16 px-8 bg-[#24283a] rounded-xl border border-white/10 text-white"
        >
          <div
            class="border-4 border-white/10 border-t-white rounded-full w-12 h-12 animate-spin mx-auto mb-4"
          />
          <p>Loading sync status...</p>
        </div>
      {:else if error && !syncStatus}
        <div
          class="text-center py-16 px-8 bg-[#24283a] rounded-xl border border-white/10 text-white"
        >
          <p class="text-xl font-bold text-red-500 mb-2">Error Loading Dashboard</p>
          <p class="text-white/70 mb-4">{error}</p>
          <button
            on:click={fetchAllData}
            class="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold cursor-pointer transition-all hover:bg-blue-500 hover:-translate-y-[1px]"
          >
            🔄 Retry
          </button>
        </div>
      {:else}
        <div class="min-w-0 overflow-hidden rounded-xl border border-white/10 bg-[#24283a] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] sm:p-5 lg:p-6">
          <KpiBarHost
            totalOpportunities={visibleTotalOpportunities}
            fullySynced={visibleFullySynced}
            syncInProgress={visibleSyncInProgress}
            conflicts={visibleConflicts}
            errors={visibleErrors}
            onKpiClick={handleKPIClick}
          />

          <NavigationTabsHost
            {activeTab}
            onTabChange={handleTabChange}
          />

          <div class="tab-content min-w-0">
            {#if activeTab === "overview"}
              <div class="overview-content flex min-w-0 flex-col gap-4">
                <SystemsOverview
                  aws={syncStatus?.aws}
                  acelink={syncStatus?.acelink}
                  hubspot={isHubspotAuthenticated ? syncStatus?.hubspot : null}
                  fullySyncedAcrossSystems={visibleFullySynced}
                  {lastSyncTime}
                  showHubspotCard={isHubspotAuthenticated}
                />
                <ActivityFeed activities={activityFeed.slice(0, 10)} showTitle={false} />
              </div>
            {/if}

            {#if activeTab === "invitations"}
              <EngagementInvitationsPanel />
            {/if}

            {#if activeTab === "opportunities"}
              <OpportunitiesTableHost
                opportunities={filteredOpportunities}
                {filterStatus}
                onSelectOpportunity={selectOpportunity}
                onFilterStatusChange={(detail) => (filterStatus = detail)}
              />
            {/if}

            {#if activeTab === "conflicts"}
              <ConflictsSummaryHost {errors} />
            {/if}

            {#if activeTab === "activity"}
              <ActivityFeed activities={activityFeed} showTitle={true} />
            {/if}
          </div>
        </div>
      {/if}
    </div>
  {/if}

  {#if drawerOpen && selectedOpportunity}
    <OpportunityDrawerHost
      opportunity={selectedOpportunity}
      onClose={closeDrawer}
      onSyncToAws={handleSyncOpportunityToAws}
      onSyncToHubspot={handleSyncOpportunityToHubspot}
    />
  {/if}
</div>
